import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Account, AccountLinks, RecoveryCodesResponse, SessionList } from '@one-deployment/api'
import { Button, Dialog, Input, Label, Select, SkeletonRows, Table, Td, Tr } from '@/components/ui'
import { Age, ErrorState } from '@/components/status'
import { EnrolFactors, RecoveryCodes } from '@/features/auth/Enrol'
import { ApiError, del, enc, get, post, put } from '@/lib/api'
import { dateTime } from '@/lib/format'
import { qk } from '@/lib/query'
import { ask } from '@/components/ask'

export function AccountPage() {
  const me = useQuery({ queryKey: qk.account(), queryFn: ({ signal }) => get<Account>('/api/account', signal) }).data
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [codes, setCodes] = useState<string[] | null>(null)
  const [cur, setCur] = useState('')
  const [next, setNext] = useState('')
  const sessions = useQuery({ queryKey: qk.accountSessions(), queryFn: ({ signal }) => get<SessionList>('/api/account/sessions', signal) })
  const pw = useMutation({ mutationFn: () => post('/api/account/password', { current: cur, next }), onSuccess: () => { setCur(''); setNext('') } })
  const act = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSettled: () => { void qc.invalidateQueries({ queryKey: qk.me() }); void qc.invalidateQueries({ queryKey: qk.account() }) },
  })
  const regen = useMutation({ mutationFn: () => post<RecoveryCodesResponse>('/api/account/recovery-codes'), onSuccess: (r) => setCodes(r.recoveryCodes) })

  if (!me) return <div className="p-5"><SkeletonRows rows={6} /></div>
  const factorCount = (me.totp ? 1 : 0) + me.passkeys.length

  return (
    <div>
      <div className="space-y-2 px-8 py-2">
        {act.error && <div className="py-3"><ErrorState error={act.error} compact /></div>}
        <Links />
        <form className="flex flex-wrap items-end gap-3 py-5" onSubmit={(e) => { e.preventDefault(); pw.mutate() }}>
          <div className="min-w-48 flex-1"><Label htmlFor="cur">Current password</Label><Input id="cur" type="password" autoComplete="current-password" required value={cur} onChange={(e) => setCur(e.target.value)} /></div>
          <div className="min-w-48 flex-1"><Label htmlFor="new">New password (12+ characters)</Label><Input id="new" type="password" autoComplete="new-password" minLength={12} required value={next} onChange={(e) => setNext(e.target.value)} /></div>
          <Button type="submit" variant="primary" size="md" disabled={pw.isPending}>Change password</Button>
          {pw.error && <div className="w-full"><ErrorState error={pw.error} compact /></div>}
          {pw.isSuccess && <p className="w-full text-xs text-ok">Password changed. Other sessions were signed out.</p>}
        </form>
        <div className="py-5">
          <div className="mb-2 flex items-center gap-3">
            <span className="text-sm text-fg">Two-factor</span>
            <span className="text-xs text-fg-subtle">{me.recoveryCodesLeft} recovery codes left{me.recoveryCodesLeft < 3 && <span className="text-warn"> · regenerate soon</span>}</span>
            <span className="ml-auto flex gap-2">
              <Button disabled={regen.isPending} onClick={() => void ask({ title: 'Regenerate recovery codes?', body: 'The old ones stop working.', confirm: 'Regenerate' }).then((ok) => ok && regen.mutate())}>Regenerate codes</Button>
              <Button onClick={() => setAdding(true)}>Add factor</Button>
            </span>
          </div>
          <Table head={['Factor', 'Added', 'Last used', '']}>
            {me.totp && (
              <Tr><Td>Authenticator app</Td>
                <Td className="text-xs text-fg-subtle">{me.totpAddedAt ? dateTime(me.totpAddedAt) : 'Unknown'}</Td>
                <Td className="text-xs text-fg-subtle">{me.totpLastUsedAt ? <Age from={me.totpLastUsedAt} /> : 'Never'}</Td>
                <Td className="text-right"><Button size="sm" variant="danger" disabled={factorCount < 2} title={factorCount < 2 ? 'Add another factor first — 2FA is compulsory' : undefined} onClick={() => act.mutate(() => del('/api/account/totp'))}>Remove</Button></Td></Tr>
            )}
            {me.passkeys.map((k) => (
              <Tr key={k.id}><Td>Passkey · {k.label}</Td><Td className="text-xs text-fg-subtle">{dateTime(k.createdAt)}</Td><Td className="text-xs text-fg-subtle">{k.lastUsedAt ? <Age from={k.lastUsedAt} /> : 'Never'}</Td>
                <Td className="text-right"><Button size="sm" variant="danger" disabled={factorCount < 2} title={factorCount < 2 ? 'Add another factor first — 2FA is compulsory' : undefined} onClick={() => act.mutate(() => del(`/api/account/passkeys/${enc(k.id)}`))}>Remove</Button></Td></Tr>
            ))}
          </Table>
        </div>
        <div className="py-5">
          <div className="mb-2 text-sm text-fg">Signed-in devices</div>
          {!sessions.data ? <SkeletonRows rows={3} /> : (
            <Table head={['Device', 'IP', 'Last seen', '']}>
              {sessions.data.sessions.map((s) => (
                <Tr key={s.id}>
                  <Td className="max-w-[40rem] truncate text-xs">{s.userAgent ?? 'Unknown'}{s.current && <span className="ml-2 text-ok">This device</span>}</Td>
                  <Td mono>{s.ip ?? '—'}</Td>
                  <Td className="text-xs text-fg-subtle"><Age from={s.lastSeenAt} /></Td>
                  <Td className="text-right">{!s.current && <Button size="sm" onClick={() => act.mutate(() => del(`/api/account/sessions/${enc(s.id)}`))}>Sign out</Button>}</Td>
                </Tr>
              ))}
            </Table>
          )}
        </div>
      </div>
      <Dialog open={adding} onOpenChange={setAdding} title="Add a factor">
        <EnrolFactors hasTotp={me.totp} onEnrolled={(r) => { setAdding(false); void qc.invalidateQueries({ queryKey: qk.me() }); void qc.invalidateQueries({ queryKey: qk.account() }); if (r.recoveryCodes?.length) setCodes(r.recoveryCodes) }} />
      </Dialog>
      <Dialog open={!!codes} onOpenChange={(o) => !o && setCodes(null)} title="Recovery codes">
        {codes && <RecoveryCodes codes={codes} onDone={() => setCodes(null)} />}
      </Dialog>
    </div>
  )
}

/**
 * Sign in with GitHub (features/25): your own sessions commit and push as you.
 * The callback lands back here with ?github=linked|failed|cancelled.
 */
function GithubLink({ linked, ready }: { linked: string | null; ready: boolean }) {
  const qc = useQueryClient()
  const [back] = useState(() => new URLSearchParams(location.search).get('github'))
  const unlink = useMutation({
    mutationFn: () => del('/api/account/github'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['account-links'] }),
  })
  if (!ready) return null
  return (
    <div className="flex flex-wrap items-center gap-3 pb-4 text-sm">
      {linked
        ? <><span>GitHub: signed in as <b>{linked}</b>. Your sessions commit and push as you.</span>
            <Button size="sm" variant="danger" className="ml-auto" disabled={unlink.isPending} onClick={() => unlink.mutate()}>Unlink</Button></>
        : <><span>Link GitHub so your sessions commit and push as you. You need it to open a session.</span>
            <a className="ml-auto" href="/api/account/github/start"><Button size="sm" variant="primary">Link GitHub</Button></a></>}
      {back === 'failed' && !linked && <span role="alert" className="w-full text-xs text-err">GitHub did not link. Try again.</span>}
      {back === 'cancelled' && !linked && <span className="w-full text-xs text-fg-faint">Linking was cancelled.</span>}
    </div>
  )
}

/** Your own GitHub, Linear and Slack accounts, so work and messages reach you there (features/17). */
function Links() {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['account-links'], queryFn: ({ signal }) => get<AccountLinks>('/api/account/links', signal) })
  const [gh, setGh] = useState('')
  const [lin, setLin] = useState('')
  const [sl, setSl] = useState('')
  useEffect(() => {
    if (q.data) { setGh(q.data.githubLogin ?? ''); setLin(q.data.linearUserId ?? ''); setSl(q.data.slackUserId ?? '') }
  }, [q.data])
  const save = useMutation({
    mutationFn: () => put<AccountLinks>('/api/account/links', { githubLogin: gh.trim() || null, linearUserId: lin || null, slackUserId: sl.trim() || null }),
    onSuccess: (v) => qc.setQueryData(['account-links'], v),
  })
  const v = q.data
  if (!v) return null
  const dirty = (v.githubLogin ?? '') !== gh.trim() || (v.linearUserId ?? '') !== lin || (v.slackUserId ?? '') !== sl.trim()
  return (
    <div className="py-5">
      <GithubLink linked={v.githubLinked ? v.githubLogin : null} ready={v.githubReady} />
      <form className="grid gap-3 text-sm sm:grid-cols-3" onSubmit={(e) => { e.preventDefault(); save.mutate() }}>
        <div className="min-w-0"><Label htmlFor="ln-gh">GitHub username</Label><Input id="ln-gh" value={gh} onChange={(e) => setGh(e.target.value)} placeholder="octocat" /></div>
        <div className="min-w-0">
          <Label htmlFor="ln-lin">Linear user</Label>
          <Select id="ln-lin" className="w-full" value={lin} onChange={(e) => setLin(e.target.value)}>
            <option value="">Not linked</option>
            {v.linearUsers.map((u) => <option key={u.id} value={u.id}>{u.name}{u.email ? ` (${u.email})` : ''}</option>)}
          </Select>
        </div>
        <div className="min-w-0"><Label htmlFor="ln-sl">Slack member id</Label><Input id="ln-sl" value={sl} onChange={(e) => setSl(e.target.value)} placeholder="U0123ABCD" /></div>
        <div className="flex items-center gap-3 sm:col-span-3">
          <Button type="submit" variant="primary" disabled={!dirty || save.isPending}>Save</Button>
          {save.isSuccess && !dirty && <span className="text-xs text-ok">Saved.</span>}
          {save.error && <span role="alert" className="text-xs text-err">{save.error instanceof ApiError ? (save.error.what ?? save.error.message) : String(save.error)}</span>}
          <span className="ml-auto text-xs text-fg-faint">{v.slackReady ? 'Slack ids are checked against the workspace.' : 'Slack is not set up yet; the id is kept until it is.'} In Slack: your profile, ⋮, Copy member ID.</span>
        </div>
      </form>
    </div>
  )
}
